import { Cpu, Gear, Kanban, Key, Plugs, Receipt, User, Users, Book, ClipboardText } from '@phosphor-icons/react';
import GlobalAssistants from './components/Utility/Icons/GlobalAssistants';
import { ReactElement } from 'react';
import React from 'react';
import { StoreSettings } from './Types/StoreTypes';

interface UserSettings {
    isAdmin: boolean;
    isSuperAdmin: boolean;
    isAvency: boolean;
    isComplianceAdmin: boolean;
    isMaintainer: boolean;
}

interface AdminLink {
    to: string;
    label: string;
    icon: ReactElement;
    navigationHeaderIcon: ReactElement;
}

const getAdminLinks = (userSettings: UserSettings, settings: StoreSettings, i18n: any, demo: boolean): AdminLink[] => {
    let menuConfig: { to: string; labelId: string; translationSource: string; icon: ReactElement }[] = [];

    if (userSettings.isAdmin) {
        menuConfig.push({ to: '/admin/dashboard', labelId: 'title', translationSource: 'Dashboard', icon: <Kanban /> });
        if (settings.prePaidActive) {
            menuConfig.push({
                to: '/admin/billing',
                labelId: 'title',
                translationSource: 'Billing',
                icon: <Receipt />,
            });
        }
        menuConfig.push({ to: '/admin/users', labelId: 'users.title', translationSource: 'Users', icon: <User /> });
        menuConfig.push({ to: '/admin/groups', labelId: 'title', translationSource: 'Groups', icon: <Users /> });
        if (settings.embeddingModel) {
            menuConfig.push({
                to: '/admin/knowledgebases',
                labelId: 'title',
                translationSource: 'Knowledgebases',
                icon: <Book />,
            });
        }
        if (settings.apiKeyLicences > 0) {
            menuConfig.push({ to: '/admin/apikeys', labelId: 'title', translationSource: 'ApiKeys', icon: <Key /> });
        }
        menuConfig.push({
            to: '/admin/assistants',
            labelId: 'title.globalAssistants',
            translationSource: 'Prompts',
            icon: <GlobalAssistants />,
        });
    }

    if (userSettings.isSuperAdmin || demo) {
        menuConfig.push({
            to: '/admin/assistantfunctions',
            labelId: 'title',
            translationSource: 'PromptFunctions',
            icon: <Plugs />,
        });
        menuConfig.push({ to: '/admin/models', labelId: 'title', translationSource: 'Models', icon: <Cpu /> });
    }

    if (userSettings.isSuperAdmin) {
        menuConfig.push({
            to: '/admin/credentials',
            labelId: 'title',
            translationSource: 'Credentials',
            icon: <Key />,
        });
    }

    if (userSettings.isMaintainer) {
        menuConfig.push({
            to: '/admin/logs',
            labelId: 'logs.title',
            translationSource: 'LogFiles',
            icon: <ClipboardText />,
        });
    }

    if (userSettings.isSuperAdmin) {
        menuConfig.push({ to: '/admin/settings', labelId: 'title', translationSource: 'Settings', icon: <Gear /> });
    }

    return menuConfig.map(({ to, labelId, translationSource, icon }) => ({
        to,
        label: i18n.translate({ id: labelId, sourceName: translationSource }),
        icon: React.cloneElement(icon as React.ReactElement<any, any>, { className: 'text-[24px] xl:text-[28px]' }),
        navigationHeaderIcon: React.cloneElement(icon as React.ReactElement<any, any>, { className: 'text-[24px]' }),
    }));
};

export const getComplianceLinks = (i18n: any): AdminLink[] => {
    const complianceConfig = [
        {
            to: '/compliance/dashboard',
            labelId: 'compliance.dashboard',
            translationSource: 'LogFiles',
            icon: <Kanban />,
        },
        {
            to: '/compliance/logs',
            labelId: 'logs.title',
            translationSource: 'LogFiles',
            icon: <ClipboardText />,
        },
    ];

    return complianceConfig.map(({ to, labelId, translationSource, icon }) => ({
        to,
        label: i18n.translate({ id: labelId, sourceName: translationSource }),
        icon: React.cloneElement(icon, { className: 'text-[24px] xl:text-[28px]' }),
        navigationHeaderIcon: React.cloneElement(icon, { className: 'text-[24px]' }),
    }));
};

export default getAdminLinks;
